/**
 * Boost API client
 *
 * Phase 4-2 / Stream 4 / Plan §12 / Design §5.7 C2 助力运营看板.
 *
 * 包装 axios apiClient 调用 admin-service /boost 端点；类型与后端 ServiceResponse 对齐。
 * 所有写动作（freeze / revert）通过 client.ts 自动注入 Idempotency-Key。
 */
import { apiClient } from './client';

// ── 漏斗 ────────────────────────────────────────────────────────────────────
export interface BoostFunnelDto {
  totalBindings: number;
  totalActivated: number;
  totalIssuedVouchers: number;
  totalUsedVouchers: number;
}

// ── inviter 排行 ────────────────────────────────────────────────────────────
export interface InviterRankingRow {
  userId: string;
  count: number;
  voucherIssued: boolean;
  voucherIssuedAt: number | null;
  lifecycle: string;
  updatedAt: number;
}

// ── 风控告警 ────────────────────────────────────────────────────────────────
export interface RiskAlertRow {
  inviterUserId: string;
  activated24h: number;
  totalCount: number;
  lastActivatedAt: number;
}

// ── 超额票审计 ─────────────────────────────────────────────────────────────
export interface OversupplyAuditRow {
  userId: string;
  count: number;
  voucherIssued: boolean;
  anomaly: 'reached_15_no_voucher' | 'voucher_not_used_30d';
  updatedAt: number;
}

// ── 反查关系 ────────────────────────────────────────────────────────────────
export interface BoostRelationshipRow {
  relationshipId: string;
  unionId: string;
  inviterUserId: string;
  workId: string | null;
  boundAt: number;
  activated: boolean;
  activatedAt: number | null;
  activatedUserId: string | null;
  superseded: boolean;
}

// ── 冻结记录 ────────────────────────────────────────────────────────────────
export type BoostFreezeStatus = 'active' | 'reverted' | 'expired';

export interface BoostFreezeRow {
  freezeId: string;
  userId: string;
  frozenBy: string;
  frozenAt: number;
  reason: string;
  canRevertUntil: number;
  status: BoostFreezeStatus;
  revertedBy: string | null;
  revertedAt: number | null;
  revertedReason: string | null;
}

interface ListResp<T> { items: T[]; total: number }

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

// ── GET endpoints ───────────────────────────────────────────────────────────

export async function getFunnel(): Promise<BoostFunnelDto> {
  const resp = await apiClient.get<ServiceResponse<BoostFunnelDto>>('/boost/funnel');
  return resp.data.data;
}

export async function getInviterRanking(limit = 50): Promise<ListResp<InviterRankingRow>> {
  const resp = await apiClient.get<ServiceResponse<ListResp<InviterRankingRow>>>(
    '/boost/inviter-ranking',
    { params: { limit } },
  );
  return resp.data.data;
}

export async function getRiskAlerts(
  windowHours = 24, threshold = 5,
): Promise<ListResp<RiskAlertRow> & { windowHours: number; threshold: number }> {
  const resp = await apiClient.get<ServiceResponse<ListResp<RiskAlertRow> & { windowHours: number; threshold: number }>>(
    '/boost/risk-alerts',
    { params: { windowHours, threshold } },
  );
  return resp.data.data;
}

export async function getOversupplyAudit(): Promise<ListResp<OversupplyAuditRow>> {
  const resp = await apiClient.get<ServiceResponse<ListResp<OversupplyAuditRow>>>(
    '/boost/oversupply-audit',
  );
  return resp.data.data;
}

export async function findRelationshipsByUnionId(
  unionId: string,
): Promise<ListResp<BoostRelationshipRow> & { unionId: string }> {
  const resp = await apiClient.get<ServiceResponse<ListResp<BoostRelationshipRow> & { unionId: string }>>(
    '/boost/relationships',
    { params: { unionId } },
  );
  return resp.data.data;
}

export async function listFreezes(
  filter: { status?: BoostFreezeStatus; userId?: string } = {},
): Promise<ListResp<BoostFreezeRow>> {
  const params: Record<string, string> = {};
  if (filter.status) params.status = filter.status;
  if (filter.userId) params.userId = filter.userId;
  const resp = await apiClient.get<ServiceResponse<ListResp<BoostFreezeRow>>>(
    '/boost/freezes', { params },
  );
  return resp.data.data;
}

export async function getFreeze(freezeId: string): Promise<BoostFreezeRow> {
  const resp = await apiClient.get<ServiceResponse<BoostFreezeRow>>(
    `/boost/freezes/${encodeURIComponent(freezeId)}`,
  );
  return resp.data.data;
}

// ── POST endpoints ──────────────────────────────────────────────────────────

export interface FreezeBoostResponse {
  freezeId: string;
  userId: string;
  canRevertUntil: number;
}

export async function freezeBoost(
  userId: string, reason: string,
): Promise<FreezeBoostResponse> {
  const resp = await apiClient.post<ServiceResponse<FreezeBoostResponse>>(
    `/boost/${encodeURIComponent(userId)}/freeze`,
    { reason },
  );
  return resp.data.data;
}

export interface RevertFreezeResponse {
  freezeId: string;
  status: 'reverted';
  revertedAt: number;
}

export async function revertFreeze(
  freezeId: string, reason: string,
): Promise<RevertFreezeResponse> {
  const resp = await apiClient.post<ServiceResponse<RevertFreezeResponse>>(
    `/boost/freezes/${encodeURIComponent(freezeId)}/revert`,
    { reason },
  );
  return resp.data.data;
}
